<script lang="ts" setup>
import { useDebounceFn } from '@vueuse/core';
import type { MaskInputOptions } from 'maska';
import { ref, reactive } from 'vue';
import type { ValidationRule } from 'vuetify';
import { getActionCodesArticles } from '../../services/api';
import type { ArticleWithActionCode } from '../../../../types/action-code';

interface Props {
  initialActionCode?: string;
  micromarketId: string;
}

const rulesCode: ValidationRule[] = [
  (v?: string) => !v || v.length >= 8 || 'Aktionscode muss mindestends 8 Zeichen lang sein.',
  (v?: string) => !v || v.length <= 20 || 'Aktionscode darf höchstens 20 Zeichen lang sein.',
];

const { initialActionCode = '', micromarketId } = defineProps<Props>();

const emit = defineEmits<{
  'apply-action-code': [actionCode: string, discountArticles: ArticleWithActionCode[]];
  'remove-action-code': [];
}>();

const maskCode = reactive<MaskInputOptions>({
  mask: 'RRRRRRRROOOOOOOOOOOO',
  tokens: {
    R: { pattern: /[a-zA-Z0-9-_]/ },
    O: { pattern: /[a-zA-Z0-9-_]/, optional: true },
  },
  eager: true,
});

const actionCode = ref<string>(initialActionCode);
const isActionCodeCheckedInBackend = ref<boolean>(false);
const isActionCodeValidInBackend = ref<boolean>();
const isCheckingActionCodeInBackend = ref<boolean>(false);
const backendValidationMessage = ref<string>('');
const showAddButton = ref<boolean>(true);

function setBackendValidationStatus(message: string, isActionCodeValid?: boolean) {
  backendValidationMessage.value = message;
  isActionCodeValidInBackend.value = isActionCodeValid;
}

async function checkActionCode(): Promise<void> {
  isActionCodeCheckedInBackend.value = false;
  isActionCodeValidInBackend.value = undefined;
  isCheckingActionCodeInBackend.value = true;
  backendValidationMessage.value = '';

  try {
    const [
      error,
      discountArticles,
    ] = await getActionCodesArticles(actionCode.value, micromarketId);

    if (error) {
      setBackendValidationStatus(error.data?.message, false);
      return;
    }

    if (!discountArticles?.length) {
      setBackendValidationStatus(
        'Für diesen Aktionscode wurden keine Artikel gefunden.',
        false,
      );
      return;
    }

    isActionCodeValidInBackend.value = true;
    emit('apply-action-code', actionCode.value, discountArticles);
  } finally {
    isActionCodeCheckedInBackend.value = true;
    isCheckingActionCodeInBackend.value = false;
  }
}

const checkActionCodeDebounced = useDebounceFn(checkActionCode, 500);

function removeActionCode() {
  actionCode.value = '';
  setBackendValidationStatus('');
  emit('remove-action-code');
}

function resetIfInvalid() {
  if (isActionCodeValidInBackend.value === false) {
    setBackendValidationStatus('');
  }
}
</script>

<template>
  <section>
    <v-row no-gutters class="mt-6">
      <v-col cols="12">
        <v-text-field
          v-model="actionCode"
          v-maska="maskCode"
          density="comfortable"
          hide-details="auto"
          color="primary"
          variant="outlined"
          :base-color="isActionCodeValidInBackend === true ? 'primary' : ''"
          :disabled="isCheckingActionCodeInBackend"
          :error="isActionCodeValidInBackend === false && !!backendValidationMessage"
          :error-messages="
            (isActionCodeValidInBackend === false && backendValidationMessage)
              ? backendValidationMessage
              : undefined
          "
          :loading="isCheckingActionCodeInBackend"
          :messages="
            (isActionCodeValidInBackend === true && backendValidationMessage)
              ? backendValidationMessage
              : undefined
          "
          :rules="rulesCode"
          :success="isActionCodeValidInBackend === true"
          @keydown.enter="checkActionCodeDebounced"
          @focus="resetIfInvalid"
        >
          <template #label>
            <v-icon class="mr-2">
              mdi-ticket
            </v-icon>
            Aktionscode
          </template>

          <template #append>
            <v-btn
              v-if="isActionCodeValidInBackend === true"
              variant="text"
              density="compact"
              color="error"
              @click="removeActionCode"
            >
              Entfernen
            </v-btn>

            <v-btn
              v-else
              variant="text"
              density="compact"
              :disabled="!actionCode"
              @click="checkActionCodeDebounced"
            >
              Hinzufügen
            </v-btn>
          </template>

          <template v-if="isActionCodeCheckedInBackend && !showAddButton" #append-inner>
            <v-icon v-if="isActionCodeValidInBackend === true" color="success">
              mdi-thumb-up
            </v-icon>

            <v-icon v-else color="error">
              mdi-alert-circle
            </v-icon>
          </template>
        </v-text-field>
      </v-col>
    </v-row>
  </section>
</template>

<style lang="css" scoped>
.v-input.v-text-field:not(.v-input--error):deep(.v-messages) {
  color: #0746a6;
  opacity: 1;
}
</style>
